import { useCallback } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Copy } from 'lucide-react';
import { cn, memoClassNames } from '@/utils';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { Button } from '@/components/button';
import { Markdown } from '@/components/markdown';
import { useSummaryContext } from './hooks/use-summary-context';
import { SummarySlots, SummaryToolbarProps } from './types';
import { SummaryToolbar } from './summary-toolbar';

const classes = memoClassNames('Summary', ['root', 'toolbar']);

export type SummaryDisplayProps = React.ComponentProps<'div'> & {
  slots?: SummarySlots;
  title?: React.ReactNode;
  asChild?: boolean;
};

export function SummaryDisplay({
  className,
  slots = {},
  title,
  asChild,
  ...others
}: SummaryDisplayProps) {
  const Body = asChild ? Slot : 'div';

  const {
    Toolbar = SummaryToolbar as React.ElementType<SummaryToolbarProps>,
    Loading,
    Empty,
  } = slots;

  const { data, loading } = useSummaryContext();
  const [_, onCopy] = useCopyToClipboard();

  const handleCopy = useCallback(() => {
    if (data) onCopy(data?.text);
  }, [data, onCopy]);

  // show the loader when needed
  if (loading) {
    return !Loading ? null : <Loading />;
  }

  // show the empty state if no summary was found
  if (!data) {
    return !Empty ? null : <Empty />;
  }

  return (
    <div {...others} className={cn(classes.root, 'mm:relative', className)}>
      <Toolbar title={title} className={classes.toolbar} onCopy={handleCopy} />
      <Body>
        <SummaryDisplaySection title="Summary">
          <Markdown content={data.text} />
        </SummaryDisplaySection>
        <SummaryDisplaySection title="Keypoints">
          <ul>
            {data.keypoints.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </SummaryDisplaySection>
        <SummaryDisplaySection title="Keywords">
          {data.keywords.join(', ')}
        </SummaryDisplaySection>
      </Body>
    </div>
  );
}

export type SummaryDisplaySectionProps = React.ComponentProps<'div'> & {
  title?: React.ReactNode;
  variant?: string;
};

export function SummaryDisplaySection({
  title,
  content,
  className,
  children,
  ...others
}: SummaryDisplaySectionProps) {
  return (
    <div className={cn('mm:relative mm:p-2', className)} {...others}>
      {title && (
        <div className="mm:text-lg mm:text-semibold mm:mb-2">{title}</div>
      )}
      <div className="mm:absolute mm:top-0 mm:right-0">
        <Button size="icon">
          <Copy />
        </Button>
      </div>
      <div>{children}</div>
    </div>
  );
}
